import { range, difference, union } from 'lodash';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as API from 'generated/api';
import { apiConfiguration } from 'utility/api';
import * as AsyncValue from 'utility/AsyncValue';
import * as FFObject from 'model/FFObject';
import { API_KEY, apiKeyHeaders, apiHost } from 'utility/api';
import unwrap from 'utility/unwrap';

export const syncChatWithURLKey = createAsyncThunk<
  API.FFPublicChat | null,
  { urlKey: string },
  { state: { cache: State } }
>(
  'cache/syncChatWithURLKey',
  async (payload: { urlKey: string }, thunkAPI) => {
    const state = thunkAPI.getState();
    const accessToken = state.cache.accessToken;

    const unauthorizedConfigParams = {
      basePath: `${apiHost}/api/v1`,
    };

    const freeformAPI = new API.FreeformApi(new API.Configuration({
      ...unauthorizedConfigParams,
      accessToken: accessToken ?? undefined,
      apiKey: API_KEY,
      headers: apiKeyHeaders(),
    }));

    return await freeformAPI.freeformPublicChatsRetrieve({
      viewUrlKey: payload.urlKey
    });
  }
);

async function pageAllObjects(
  accessToken: string | null,
  requestParams: API.FreeformObjectsListRequest,
): Promise<Record<string, API.FFObject>> {
  const unauthorizedConfigParams = {
    basePath: `${apiHost}/api/v1`,
  };

  const freeformAPI = new API.FreeformApi(new API.Configuration({
    ...unauthorizedConfigParams,
    accessToken: accessToken ?? undefined,
    apiKey: API_KEY,
    headers: apiKeyHeaders(),
  }));

  let nextPageIndex: number | null = 1;
  let accumulator: Record<string, API.FFObject> = {};

  while (nextPageIndex != null) {
    const page: API.PaginatedFFObjectList = await freeformAPI.freeformObjectsList({
      pageSize: 200, // increase page size to decrease number of requests on dense pages
      page: nextPageIndex,
      ...requestParams,
    });
    nextPageIndex = page.next == null ? null : unwrap(nextPageIndex) + 1;
    for (const obj of (page.results ?? [])) {
      if (obj.uuid == null) {
        continue;
      }
      accumulator[obj.uuid] = obj;
    }
  }

  return accumulator;
}

// BEGIN PAGING HELPERS

// To throttle requests to the backend, group requests for objects into pages,
// and don't re-request pages that we've already requested.

const pageHeight = 5000;

function pageIndexContaining(y: number): number {
  return Math.floor(y / pageHeight);
}

function topOfPage(pageIndex: number): number {
  return pageIndex * pageHeight;
}

function boundsForPageRange(minPage: number, maxPage: number): [number, number] {
  return [
    topOfPage(minPage),
    topOfPage(maxPage + 1),
  ];
}

// END PAGING HELPERS

export const syncObjectsWithinRange = createAsyncThunk<
  { objects: Record<string, API.FFObject>, nextLoadedChatPages: number[] },
  { chatID: number, minContentY: number, maxContentY: number },
  { state: { cache: State } }
>(
  'cache/syncObjectsWithinRange',
  async (payload: { chatID: number, minContentY: number, maxContentY: number }, thunkAPI) => {
    const state = thunkAPI.getState();
    const accessToken = state.cache.accessToken;
    const pageLoadMargin = 1;
    const pagesToLoad = range(
      pageIndexContaining(payload.minContentY) - pageLoadMargin,
      pageIndexContaining(payload.maxContentY) + 1 + pageLoadMargin
    );

    const pagesToLoadWithoutAlreadyLoaded = difference(pagesToLoad, state.cache.loadedChatPages);
    const rangesToLoad = mergeContiguousRanges(pagesToLoadWithoutAlreadyLoaded.map(page => boundsForPageRange(page, page)));

    const objects: Record<string, API.FFObject> = {};

    for (const [minY, maxY] of rangesToLoad) {
      const response = await pageAllObjects(accessToken, {
        chat: payload.chatID,
        minY,
        maxY,
      });
      Object.assign(objects, response);
    }

    return {
      objects,
      nextLoadedChatPages: union(
        pagesToLoadWithoutAlreadyLoaded,
        state.cache.loadedChatPages,
      ),
    };
  }
);

export const authenticateUser = createAsyncThunk(
  'cache/authenticateUser',
  async (payload: {username: string, password: string}) => {
    const authAPI = new API.AuthApi(apiConfiguration());

    // need to use raw API here because OpenAPI spec for this endpoint reports an incorrect response shape
    const response = await authAPI.authTokenCreateRaw({
      customTokenObtainPair: {
        username: payload.username,
        password: payload.password,
      }
    });
    const json = await response.raw.json();
    const {access: accessToken} = json;
    return accessToken;
  }
);

export interface State {
  counter: number;
  accessToken: string | null;
  assetURLs: Record<string, AsyncValue.AsyncValue<string>>;

  // chat view key -> chat
  chats: Record<string, AsyncValue.AsyncValue<API.FFPublicChat>>;

  // implicitly for only active chat :/
  loadedChatPages: Array<number>;

  // { [obj.uuid]: obj }
  objects: Record<string, AsyncValue.AsyncValue<FFObject.FFObject>>;
}

const initialState: State = {
  counter: 0,
  accessToken: null,
  assetURLs: {},
  chats: {},
  objects: {},
  loadedChatPages: [],
};

const cacheSlice = createSlice({
  name: 'cache',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(authenticateUser.fulfilled, (state, action) => {
        state.accessToken = action.payload;
      })
      .addCase(syncChatWithURLKey.pending, (state, action) => {
        state.chats[action.meta.arg.urlKey] = AsyncValue.fetching();
      })
      .addCase(syncChatWithURLKey.rejected, (state, action) => {
        state.chats[action.meta.arg.urlKey] = AsyncValue.error();
      })
      .addCase(syncChatWithURLKey.fulfilled, (state, action) => {
        const chat = action.payload;
        if (chat == null) {
          return;
        }
        state.chats[action.meta.arg.urlKey] = AsyncValue.fetched(chat);
      })
      .addCase(syncObjectsWithinRange.fulfilled, (state, action) => {
        const { objects, nextLoadedChatPages } = action.payload;
        for (const key of Object.keys(objects)) {
          const object = objects[key];
          const proto = FFObject.fromProtobufStr(object.protobuf);
          
          state.objects[key] = AsyncValue.fetched(proto);

          const assetUUID = proto.getAsset()?.getUuid();
          const assetURL = object.assetUrl == null ? null : (object.assetUrl as any)['original'] as string;

          if (assetUUID != null && assetURL != null) {
            state.assetURLs[assetUUID] = AsyncValue.fetched(assetURL);
          }
        }
        if (!eqSet(state.loadedChatPages, nextLoadedChatPages)) {
          state.loadedChatPages = nextLoadedChatPages;
        }
      })
  },
});

function eqSet<T>(as: Array<T>, bs: Array<T>): boolean {
  if (as.length !== bs.length) return false;
  for (const a of as) if (!bs.includes(a)) return false;
  return true;
}

/*
 * The runtime of this fn is O(ranges.length^2) - do not use with large
 * `ranges`.
 *
 *     mergeContiguousRanges([[0, 1], [1, 3], [2, 4], [50, 100]])
 *     => [[0, 4], [50, 100]]
 */
function mergeContiguousRanges(ranges: Array<[number, number]>): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const range of ranges) {
    const indexOfRangeToMerge = out.findIndex((rangeToMerge) => (
      ((range[0] <= rangeToMerge[0]) && (range[1] >= rangeToMerge[0]))
      || ((range[0] <= rangeToMerge[1]) && (range[1] >= rangeToMerge[1]))
    ));
    if (indexOfRangeToMerge === -1) {
      out.push(range);
    } else {
      const rangeToMerge = ranges[indexOfRangeToMerge];
      if (range[0] < rangeToMerge[0]) {
        rangeToMerge[0] = range[0];
      }
      if (range[1] > rangeToMerge[1]) {
        rangeToMerge[1] = range[1];
      }
      out[indexOfRangeToMerge] = rangeToMerge;
    }
  }
  return out;
}

export default cacheSlice;
